import { Injectable } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import DOMPurify from 'dompurify';

@Injectable({
  providedIn: 'root'
})
export class NotificationHtmlSanitizerService {
  private static readonly allowedTags = [
    'p', 'br', 'span', 'strong', 'b', 'em', 'i', 'u', 's',
    'ul', 'ol', 'li', 'a', 'div',
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6'
  ];

  private static readonly allowedAttributes = ['href', 'target', 'rel', 'style'];

  private static readonly allowedStyles = [
    'color',
    'background-color',
    'text-align',
    'font-size',
    'font-weight',
    'font-style',
    'text-decoration',
    'line-height',
    'list-style-type',
    'padding-left',
    'margin-left'
  ];

  constructor(private readonly domSanitizer: DomSanitizer) {}

  sanitize(
    content: string | null | undefined,
    options?: { preserveStyles?: boolean }
  ): SafeHtml {
    const dirtyHtml = content || '';
    const preserveStyles = options?.preserveStyles ?? true;

    const cleaned = DOMPurify.sanitize(dirtyHtml, {
      ALLOWED_TAGS: NotificationHtmlSanitizerService.allowedTags,
      ALLOWED_ATTR: NotificationHtmlSanitizerService.allowedAttributes,
      ALLOW_DATA_ATTR: false,
      FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form', 'input', 'button', 'style']
    }) as string;

    const normalized = this.normalizeMarkup(cleaned, preserveStyles);
    return this.domSanitizer.bypassSecurityTrustHtml(normalized);
  }

  private normalizeMarkup(html: string, preserveStyles: boolean): string {
    const root = document.createElement('div');
    root.innerHTML = html;

    root.querySelectorAll<HTMLElement>('[style]').forEach((element) => {
      if (!preserveStyles) {
        element.removeAttribute('style');
        return;
      }

      const rules: string[] = [];

      NotificationHtmlSanitizerService.allowedStyles.forEach((property) => {
        const value = element.style.getPropertyValue(property)?.trim();
        if (value) {
          rules.push(`${property}: ${value}`);
        }
      });

      if (rules.length === 0) {
        element.removeAttribute('style');
      } else {
        element.setAttribute('style', rules.join('; '));
      }
    });

    root.querySelectorAll<HTMLAnchorElement>('a').forEach((anchor) => {
      const href = (anchor.getAttribute('href') || '').trim();
      const safeHref = /^(https?:|mailto:|tel:|#|\/)/i.test(href);

      if (!safeHref) {
        anchor.removeAttribute('href');
      }

      if (anchor.getAttribute('target') === '_blank') {
        anchor.setAttribute('rel', 'noopener noreferrer');
      }
    });

    return root.innerHTML;
  }
}
